import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AccountDeletionDetailModal = ({ show, request, onHide, onApprove, onReject, onReapprove, formatDate, formatStatus }) => {
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isRejectProcessing, setIsRejectProcessing] = useState(false);

    useEffect(() => {
        if (!show) {
            setRejectReason('');
            setShowRejectModal(false);
            setIsProcessing(false);
            setIsRejectProcessing(false);
        }
    }, [show]);

    const handleApprove = async () => {
        setIsProcessing(true);
        try {
            await onApprove(request);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReapprove = async () => {
        setIsProcessing(true);
        try {
            await onReapprove(request);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            toast.error('Veuillez saisir un motif de rejet');
            return;
        }
        if (rejectReason.length > 500) {
            toast.error('Le motif de rejet ne doit pas dépasser 500 caractères');
            return;
        }
        setIsRejectProcessing(true);
        try {
            await onReject(request, rejectReason);
            setShowRejectModal(false);
        } finally {
            setIsRejectProcessing(false);
        }
    };

    // Fonction pour obtenir le badge de statut avec les nouveaux statuts
    const getStatusBadge = (status) => {
        switch(status) {
            case 'TOKEN_VALIDATED':
                return <span className="badge bg-info text-dark" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>En attente de validation admin</span>;
            case 'APPROVED':
                return <span className="badge bg-success" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>Approuvé</span>;
            case 'REJECTED':
                return <span className="badge bg-danger" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>Rejeté</span>;
            case 'PROCESSED':
                return <span className="badge bg-secondary" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>Compte supprimé</span>;
            case 'PENDING_TOKEN':
                return <span className="badge bg-warning text-dark" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>En attente validation utilisateur</span>;
            default:
                return <span className="badge bg-secondary" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>{status}</span>;
        }
    };

    // Fonction pour obtenir l'icône du statut
    const getStatusIcon = () => {
        switch(request?.status) {
            case 'TOKEN_VALIDATED':
                return <i className="fas fa-hourglass-half text-info me-2"></i>;
            case 'APPROVED':
                return <i className="fas fa-check-circle text-success me-2"></i>;
            case 'REJECTED':
                return <i className="fas fa-times-circle text-danger me-2"></i>;
            case 'PROCESSED':
                return <i className="fas fa-trash-alt text-secondary me-2"></i>;
            case 'PENDING_TOKEN':
                return <i className="fas fa-clock text-warning me-2"></i>;
            default:
                return null;
        }
    };

    // Déterminer quels boutons afficher selon le statut
    const getActionButtons = () => {
        switch(request?.status) {
            case 'TOKEN_VALIDATED':
                // Demande validée par l'utilisateur, en attente de décision admin
                return (
                    <>
                        <Button
                            variant="danger"
                            onClick={() => setShowRejectModal(true)}
                            disabled={isProcessing || isRejectProcessing}
                        >
                            {isRejectProcessing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Rejet en cours...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-times me-2"></i>
                                    Rejeter
                                </>
                            )}
                        </Button>
                        <Button
                            variant="success"
                            onClick={handleApprove}
                            disabled={isProcessing || isRejectProcessing}
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Suppression en cours...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-trash-alt me-2"></i>
                                    Approuver et supprimer le compte
                                </>
                            )}
                        </Button>
                    </>
                );

            case 'REJECTED':
                // Demande rejetée, possibilité de réapprouver
                return (
                    <Button
                        variant="success"
                        onClick={handleReapprove}
                        disabled={isProcessing || isRejectProcessing}
                    >
                        {isProcessing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Réapprobation en cours...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-redo-alt me-2"></i>
                                Réapprouver la demande
                            </>
                        )}
                    </Button>
                );

            case 'APPROVED':
            case 'PROCESSED':
            case 'PENDING_TOKEN':
            default:
                // Aucune action disponible
                return null;
        }
    };

    if (!request) return null;

    return (
        <>
            <Modal
                show={show}
                onHide={onHide}
                size="lg"
                backdrop="static"
                keyboard={true}
                style={{ zIndex: 1060 }}
            >
                <Modal.Header closeButton={!isProcessing && !isRejectProcessing}>
                    <Modal.Title>
                        {getStatusIcon()}
                        Détails de la demande de suppression
                        <span className="ms-3">{getStatusBadge(request.status)}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4">
                        <h5 className="mb-3">Informations du demandeur</h5>
                        <div className="border p-3 rounded bg-light">
                            <p><strong>Nom complet :</strong> {request.user?.firstName} {request.user?.lastName}</p>
                            <p><strong>Nom d'utilisateur :</strong> @{request.username}</p>
                            <p><strong>Email :</strong> {request.email}</p>
                            <p><strong>Téléphone :</strong> {request.user?.phoneNumber || 'Non renseigné'}</p>
                        </div>
                    </div>

                    <div className="mb-4">
                        <h5 className="mb-3">Détails de la demande</h5>
                        <div className="border p-3 rounded bg-light">
                            <p><strong>Date de la demande :</strong> {formatDate(request.createdAt)}</p>
                            <p><strong>Raison de la suppression :</strong> {request.deletionReason}</p>
                            {request.comment && (
                                <p><strong>Commentaire :</strong> {request.comment}</p>
                            )}
                            <p><strong>Confirmation :</strong> {request.confirmation ? 'Oui' : 'Non'}</p>
                            {request.tokenExpirationDate && request.status === 'TOKEN_VALIDATED' && (
                                <p><strong>Token validé le :</strong> {formatDate(request.modifiedAt)}</p>
                            )}
                        </div>
                    </div>

                    {request.status === 'REJECTED' && request.rejectionReason && (
                        <div className="mb-4">
                            <h5 className="mb-3">Motif du rejet</h5>
                            <div className="alert alert-danger">
                                <i className="fas fa-exclamation-circle me-2"></i>
                                {request.rejectionReason}
                            </div>
                        </div>
                    )}

                    {/* Messages d'information selon le statut */}
                    {request.status === 'TOKEN_VALIDATED' && (
                        <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            <strong>Demande prête à être traitée :</strong> L'utilisateur a validé son token.
                            Vous pouvez maintenant approuver la suppression définitive du compte ou la rejeter.
                        </div>
                    )}

                    {request.status === 'REJECTED' && (
                        <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <strong>Demande rejetée :</strong> Cette demande a été rejetée.
                            Vous pouvez la réapprouver si la situation change.
                        </div>
                    )}

                    {request.status === 'APPROVED' && (
                        <div className="alert alert-success">
                            <i className="fas fa-check-circle me-2"></i>
                            <strong>Demande approuvée :</strong> Le compte a été marqué comme approuvé.
                            La suppression sera effective prochainement.
                        </div>
                    )}

                    {request.status === 'PROCESSED' && (
                        <div className="alert alert-secondary">
                            <i className="fas fa-trash-alt me-2"></i>
                            <strong>Compte supprimé :</strong> Le compte a été définitivement supprimé.
                            Cette action est irréversible.
                        </div>
                    )}

                    {request.status === 'PENDING_TOKEN' && (
                        <div className="alert alert-warning">
                            <i className="fas fa-clock me-2"></i>
                            <strong>En attente de validation utilisateur :</strong>
                            Un token a été envoyé à l'utilisateur. La demande sera traitable une fois le token validé.
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {getActionButtons()}
                    <Button
                        variant="secondary"
                        onClick={onHide}
                        disabled={isProcessing || isRejectProcessing}
                    >
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de rejet avec champ de saisie */}
            <Modal
                show={showRejectModal}
                onHide={() => {
                    if (!isRejectProcessing) {
                        setShowRejectModal(false);
                        setRejectReason('');
                    }
                }}
                centered
                style={{ zIndex: 2000 }}
                backdrop="static"
                keyboard={!isRejectProcessing}
            >
                <Modal.Header closeButton={!isRejectProcessing}>
                    <Modal.Title>
                        <i className="fas fa-ban text-danger me-2"></i>
                        Motif du rejet
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Veuillez saisir le motif du rejet :</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Expliquez pourquoi cette demande est rejetée..."
                            maxLength={500}
                            disabled={isRejectProcessing}
                        />
                        <Form.Text className="text-muted">
                            {rejectReason.length}/500 caractères
                        </Form.Text>
                    </Form.Group>
                    <div className="alert alert-info mt-2">
                        <small>
                            <i className="fas fa-info-circle me-1"></i>
                            L'utilisateur recevra une notification avec ce motif de rejet.
                        </small>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowRejectModal(false);
                            setRejectReason('');
                        }}
                        disabled={isRejectProcessing}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleReject}
                        disabled={!rejectReason.trim() || isRejectProcessing}
                    >
                        {isRejectProcessing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Traitement en cours...
                            </>
                        ) : (
                            'Confirmer le rejet'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AccountDeletionDetailModal;